import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import {
  type AlationConfig,
  type AlationConfigDraft,
  type AlationConnectionTest,
  type AlationSemanticSyncStatus,
  isAlationConfig,
  isAlationConnectionTest,
  isAlationSemanticSyncStatus,
} from "@/features/data-hub/model/alation";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

export type AlationState = {
  domainId: number | null;
  config: AlationConfig | null;
  status: RequestStatus;
  error: string | null;
  saveStatus: RequestStatus;
  saveError: string | null;
  testStatus: RequestStatus;
  testError: string | null;
  testResult: AlationConnectionTest | null;
  /** Latest catalog-sync run for this domain. `null` until the first status read lands. */
  syncStatus: AlationSemanticSyncStatus | null;
  /** The status *request*, not the run — the run's own state is `syncStatus.running`. */
  syncStatusRequest: RequestStatus;
  startSyncStatus: RequestStatus;
  startSyncError: string | null;
};

export const alationInitialState: AlationState = {
  domainId: null,
  config: null,
  status: STATUS.IDLE,
  error: null,
  saveStatus: STATUS.IDLE,
  saveError: null,
  testStatus: STATUS.IDLE,
  testError: null,
  testResult: null,
  syncStatus: null,
  syncStatusRequest: STATUS.IDLE,
  startSyncStatus: STATUS.IDLE,
  startSyncError: null,
};

function alationPath(domainId: number, suffix: string): string {
  return `/v1/domains/${domainId}/alation${suffix}`;
}

function draftToBody(draft: AlationConfigDraft): Record<string, unknown> {
  return {
    base_url: draft.baseUrl.trim(),
    client_id: draft.clientId.trim(),
    client_secret: draft.clientSecret.trim(),
  };
}

export const fetchAlationConfig = createAsyncThunk<
  { domainId: number; config: AlationConfig },
  number,
  { rejectValue: string }
>("alation/fetchConfig", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(alationPath(domainId, "/config"));
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAlationConfig(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { domainId, config: data };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

/**
 * Test without saving. The backend performs the full handshake — OAuth token, MCP `initialize`,
 * `tools/list` — so a success here means the credentials genuinely work.
 */
export const testAlationCredentials = createAsyncThunk<
  AlationConnectionTest,
  { domainId: number; draft: AlationConfigDraft },
  { rejectValue: string }
>("alation/testCredentials", async ({ domainId, draft }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(alationPath(domainId, "/test"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draftToBody(draft)),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAlationConnectionTest(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

/**
 * Connect, and the update path too: the row is unique per domain, so re-submitting replaces what
 * is stored. The backend re-tests before writing, so a rejection here means the credentials
 * failed and whatever was stored before is still in place.
 */
export const saveAlationConfig = createAsyncThunk<
  AlationConfig,
  { domainId: number; draft: AlationConfigDraft },
  { rejectValue: string }
>("alation/saveConfig", async ({ domainId, draft }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(alationPath(domainId, "/config"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(draftToBody(draft)),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAlationConfig(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

/**
 * Kick off a catalog sync. Answers 202 with a job id — the run itself is polled through
 * `fetchAlationSyncStatus`, because it is minutes of Alation round trips.
 */
export const startAlationSemanticSync = createAsyncThunk<
  { domainId: number },
  number,
  { rejectValue: string }
>("alation/startSemanticSync", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(alationPath(domainId, "/semantic-sync"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return { domainId };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

/**
 * Read the latest run. Also what tells the Enrichment tab whether Alation is connected at all —
 * `configured` rides on this response so the tab needs one poll rather than two calls.
 */
export const fetchAlationSyncStatus = createAsyncThunk<
  AlationSemanticSyncStatus,
  number,
  { rejectValue: string }
>("alation/fetchSyncStatus", async (domainId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(alationPath(domainId, "/semantic-sync"));
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isAlationSemanticSyncStatus(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Request failed");
  }
});

const alationSlice = createSlice({
  name: "alation",
  initialState: alationInitialState,
  reducers: {
    clearAlation: () => alationInitialState,
    clearAlationSyncError: (state) => {
      state.startSyncError = null;
      state.startSyncStatus = STATUS.IDLE;
    },
    clearAlationSaveError: (state) => {
      state.saveError = null;
      state.testError = null;
      state.testResult = null;
      state.saveStatus = STATUS.IDLE;
      state.testStatus = STATUS.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlationConfig.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchAlationConfig.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.domainId = action.payload.domainId;
        state.config = action.payload.config;
      })
      .addCase(fetchAlationConfig.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Could not load the Alation configuration.";
      })
      .addCase(testAlationCredentials.pending, (state) => {
        state.testStatus = STATUS.LOADING;
        state.testError = null;
        state.testResult = null;
      })
      .addCase(testAlationCredentials.fulfilled, (state, action) => {
        state.testStatus = STATUS.SUCCEEDED;
        state.testResult = action.payload;
      })
      .addCase(testAlationCredentials.rejected, (state, action) => {
        state.testStatus = STATUS.FAILED;
        state.testError = action.payload ?? "Could not connect to Alation.";
      })
      .addCase(saveAlationConfig.pending, (state) => {
        state.saveStatus = STATUS.LOADING;
        state.saveError = null;
      })
      .addCase(saveAlationConfig.fulfilled, (state, action) => {
        state.saveStatus = STATUS.SUCCEEDED;
        state.config = action.payload;
      })
      .addCase(saveAlationConfig.rejected, (state, action) => {
        state.saveStatus = STATUS.FAILED;
        state.saveError = action.payload ?? "Could not save the Alation configuration.";
      })
      .addCase(startAlationSemanticSync.pending, (state) => {
        state.startSyncStatus = STATUS.LOADING;
        state.startSyncError = null;
      })
      .addCase(startAlationSemanticSync.fulfilled, (state) => {
        state.startSyncStatus = STATUS.SUCCEEDED;
        // Marked running here rather than waiting for the next poll, so the menu item disables
        // and the panel switches to its running state on the same click that started the run.
        if (state.syncStatus !== null) {
          state.syncStatus = { ...state.syncStatus, running: true, status: "running", error: null };
        }
      })
      .addCase(startAlationSemanticSync.rejected, (state, action) => {
        state.startSyncStatus = STATUS.FAILED;
        state.startSyncError = action.payload ?? "Could not start the Alation sync.";
      })
      .addCase(fetchAlationSyncStatus.pending, (state) => {
        state.syncStatusRequest = STATUS.LOADING;
      })
      .addCase(fetchAlationSyncStatus.fulfilled, (state, action) => {
        state.syncStatusRequest = STATUS.SUCCEEDED;
        state.syncStatus = action.payload;
      })
      .addCase(fetchAlationSyncStatus.rejected, (state) => {
        // Deliberately not surfaced as an error: this is a poll, and one failed tick must not
        // replace a finished run's summary with a network message.
        state.syncStatusRequest = STATUS.FAILED;
      });
  },
});

export const { clearAlation, clearAlationSaveError, clearAlationSyncError } =
  alationSlice.actions;

export const alationReducer = alationSlice.reducer;
